import { useSignal } from "@preact/signals";
import { useCallback, useRef } from "preact/hooks";

import { ApplicationMenu } from "./components/ApplicationMenu.js";
import { ApplicationStatus } from "./components/ApplicationStatus.js";
import { ApplicationWorkspace } from "./components/ApplicationWorkspace.js";
import { QuickSearch } from "./components/QuickSearch.js";
import { useApplicationShortcuts } from "./hooks/useApplicationShortcuts.js";
import { useFileDrop } from "./hooks/useFileDrop.js";
import { workspace } from "./model/WorkspaceStore.js";

import styles from "./App.module.css";

export function App() {
  const fileInput = useRef<HTMLInputElement>(null);
  const quickSearchOpen = useSignal(false);

  const addFiles = useCallback((files: readonly File[]): void => {
    void workspace.addFiles(files);
  }, []);

  const chooseFiles = useCallback((): void => {
    fileInput.current?.click();
  }, []);

  const showQuickSearch = useCallback((): void => {
    quickSearchOpen.value = true;
  }, [quickSearchOpen]);

  const closeActiveTab = useCallback((): boolean => {
    const activeTabId = workspace.activeTabId.value;
    if (activeTabId === null) {
      return false;
    }
    workspace.closeTab(activeTabId);
    return true;
  }, []);

  const dismissTransientUi = useCallback((): void => {
    quickSearchOpen.value = false;
  }, [quickSearchOpen]);

  useApplicationShortcuts({
    onChooseFiles: chooseFiles,
    onShowQuickSearch: showQuickSearch,
    onCloseActiveTab: closeActiveTab,
    onDismissTransientUi: dismissTransientUi,
  });

  const fileDrop = useFileDrop(addFiles);
  const artifacts = workspace.artifacts.value;
  const tabs = workspace.tabs.value;

  return (
    <div
      class={styles.app}
      onDragEnter={fileDrop.onDragEnter}
      onDragOver={fileDrop.onDragOver}
      onDragLeave={fileDrop.onDragLeave}
      onDrop={fileDrop.onDrop}
    >
      <ApplicationMenu
        fileInput={fileInput}
        hasWorkspaceContent={artifacts.length > 0 || tabs.length > 0}
        canSave={artifacts.some((artifact) => artifact.classCount > 0)}
        savingZip={workspace.savingZip.value}
        onFilesSelected={addFiles}
        onChooseFiles={chooseFiles}
        onClearWorkspace={() => workspace.clearWorkspace()}
        onSaveZip={() => {
          void workspace.saveDecompiledZip();
        }}
        onShowQuickSearch={showQuickSearch}
      />

      <ApplicationWorkspace
        artifacts={artifacts}
        tabs={tabs}
        activeTabId={workspace.activeTabId.value}
        onOpenClass={(reference, target, relativePath) =>
          workspace.openClass(reference, target, relativePath)
        }
        onOpenFile={(reference) => workspace.openFile(reference)}
        onUnloadArtifact={(artifactId) => workspace.removeArtifact(artifactId)}
        onActivateTab={(tabId) => workspace.activateTab(tabId)}
        onCloseTab={(tabId) => workspace.closeTab(tabId)}
        onCloseAllTabs={() => workspace.closeAllTabs()}
        onCloseOtherTabs={(tabId) => workspace.closeOtherTabs(tabId)}
        onCopyRelativePath={(relativePath) => {
          void workspace.copyRelativePath(relativePath);
        }}
        onExportDecompiledSource={(reference) => {
          void workspace.exportDecompiledSource(reference);
        }}
        onChooseFiles={chooseFiles}
      />

      {quickSearchOpen.value ? (
        <QuickSearch
          artifacts={artifacts}
          onClose={() => {
            quickSearchOpen.value = false;
          }}
          onOpenResult={(result) => workspace.openSearchResult(result)}
        />
      ) : null}

      <ApplicationStatus
        notice={workspace.notice.value}
        zipProgress={workspace.zipProgress.value}
        busy={workspace.loadingFiles.value}
        dragActive={fileDrop.dragActive}
        onDismissNotice={() => workspace.dismissNotice()}
      />
    </div>
  );
}
